import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "i18next";

const baseURL = process.env.REACT_APP_BASE_URL;
const user = JSON.parse(localStorage.getItem("userElwnsh"));
const token = user?.token;

axios.defaults.headers.common["Authorization"] = token ? `Bearer ${token}` : "";
axios.defaults.headers.common["Lang"] = i18n.language;

i18n.on("languageChanged", (lng) => {
  axios.defaults.headers.common["Lang"] = lng;
});

// Fetch Compensation Categories (optional status/deleted/type filters)
export const fetchCompensations = createAsyncThunk(
  "compensationCategories/fetchAll",
  async ({ page = 1, status, deleted, type } = {}, thunkAPI) => {
    try {
      const params = { page };
      if (status) {
        params.status = status;
      }
      if (deleted !== undefined && deleted !== null && deleted !== "") {
        params.deleted = String(deleted);
      }
      if (type) {
        params.type = type;
      }
      const response = await axios.get(`${baseURL}/compensation-categories`, {
        params,
      });
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Add New Compensation Category
export const addCompensation = createAsyncThunk(
  "compensationCategories/add",
  async (formData, thunkAPI) => {
    try {
      const response = await axios.post(
        `${baseURL}/compensation-categories/store`,
        formData
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update Compensation Status
export const updateCompensation = createAsyncThunk(
  "compensationCategories/update",
  async ({ id, formData }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${baseURL}/compensation-categories/update`,
        { compensation_category_id: id, ...formData }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete Compensation
export const deleteCompensation = createAsyncThunk(
  "compensationCategories/delete",
  async (id, thunkAPI) => {
    try {
      await axios.post(`${baseURL}/compensation-categories/delete`, {
        compensation_category_id: id,
      });
      return { id };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);
export const changeCompensationStatus = createAsyncThunk(
  "compensationCategories/changeStatus",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/compensation-categories/change-status`,
        { compensation_category_id: id }
      );
      return res.data.data; // backend should return the updated category
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const restoreCompensation = createAsyncThunk(
  "compensationCategories/restore",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/compensation-categories/reapprove`,
        { compensation_category_id: id }
      );
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);
// get load types record
export const fetchRecord = createAsyncThunk(
  "compensationCategories/fetchRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseURL}/compensation-categories/show/${id}`
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue({ message });
    }
  }
);
const compensationCategoriesSlice = createSlice({
  name: "compensationCategories",
  initialState: {
    data: [],
    loading: false,
    record: null,
    error: null,
    success: false,
    pagination: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchCompensations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompensations.fulfilled, (state, action) => {
        state.loading = false;
        const incoming = action.payload;
        const arg = action.meta.arg || {};
        const append = Boolean(arg.append);
        const rows = incoming?.data || [];
        if (append && Array.isArray(state.data)) {
          const seen = new Set(state.data.map((r) => String(r?.id)));
          const merged = [...state.data];
          for (const row of rows) {
            if (row?.id == null) continue;
            const k = String(row.id);
            if (seen.has(k)) continue;
            seen.add(k);
            merged.push(row);
          }
          state.data = merged;
        } else {
          state.data = rows;
        }
        state.pagination = incoming?.pagination || null;
      })
      .addCase(fetchCompensations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // Add
      .addCase(addCompensation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(addCompensation.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
      })
      .addCase(addCompensation.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload || "Something went wrong";
      })
      // Update Compensation
      .addCase(updateCompensation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateCompensation.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const updated = action.payload;
        if (Array.isArray(state.data) && updated?.id) {
          const idx = state.data.findIndex((item) => item.id === updated.id);
          if (idx !== -1) {
            state.data[idx] = updated;
          }
        }
      })
      .addCase(updateCompensation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update compensation";
        state.success = false;
      })
      .addCase(fetchRecord.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecord.fulfilled, (state, action) => {
        state.loading = false;
        state.record = action.payload.data;
      })
      .addCase(fetchRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || "Failed to add data";
      })
      // 🗑 Delete Compensation
      .addCase(deleteCompensation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteCompensation.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(deleteCompensation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete compensation";
        state.success = false;
      })
      .addCase(changeCompensationStatus.fulfilled, (state) => {
        state.loading = false;
      })

      .addCase(restoreCompensation.fulfilled, (state) => {
        state.loading = false;
      });
  },
});
export const { clearState } = compensationCategoriesSlice.actions;
export default compensationCategoriesSlice.reducer;
